import type { DocumentPreviewPanelProps } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useActiveDocumentPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useActiveDocumentPreview'
import type {
  DocumentSearchResult,
  Highlight,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SearchActionsContextType } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { RolodexEntryType } from 'types'

export interface UseDocumentPreviewPanelStateParams {
  activeIndex: number
  contentFilter: DocumentsViewerContentFilter
  documentResults: DocumentTableRow[]
  elasticsearchHighlightsMap: Map<string, Highlight[]>
  filteredDocuments: DocumentTableRow[]
  isLinkedDocsMode: boolean
  onOpenNewTab: SearchActionsContextType['onOpenNewTab']
  onSearchDocumentNavigate: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean
  ) => void
  rawSearchData: DocumentSearchResult | undefined
  searchQuery: string
  selectedDocumentId: string | null
  showIndividualScores: boolean
  viewMode: ViewMode
}

export interface UseDocumentPreviewPanelStateResult {
  activeDocument: DocumentTableRow | null
  detailScores: Record<string, number> | undefined
  documentPreviewPanelProps: Omit<DocumentPreviewPanelProps, 'onAddToContext'>
  handleOpenPreview: () => void
  isLoadingPreview: boolean
  isPreviewOpen: boolean
}

export function useDocumentPreviewPanelState({
  activeIndex,
  contentFilter,
  documentResults,
  elasticsearchHighlightsMap,
  filteredDocuments,
  isLinkedDocsMode,
  onOpenNewTab,
  onSearchDocumentNavigate,
  rawSearchData,
  searchQuery,
  selectedDocumentId,
  showIndividualScores,
  viewMode,
}: UseDocumentPreviewPanelStateParams): UseDocumentPreviewPanelStateResult {
  const { siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const selectedDocumentLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading ||
      !selectedDocumentId ||
      viewMode !== 'table'
        ? undefined
        : {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            ids: [selectedDocumentId],
            tags: getPipelineConfigScopedTags({ site: siteID }),
            limit: 1,
          },
    [
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      selectedDocumentId,
      siteID,
      viewMode,
    ]
  )
  const {
    data: selectedDocumentEntries,
    isLoading: isLoadingSelectedDocument,
  } = useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
    selectedDocumentLoadOptions
  )

  const selectedTableDocument = useMemo(() => {
    if (!selectedDocumentEntries?.[0]) return null
    return transformSystemDocumentToTableRow(selectedDocumentEntries[0])
  }, [selectedDocumentEntries])

  const {
    activeDocument,
    activeDocumentHighlights,
    relevanceScore,
    detailScores,
    isPreviewOpen,
    isLoadingPreview,
  } = useActiveDocumentPreview({
    documentResults: viewMode === 'card' ? filteredDocuments : documentResults,
    activeIndex,
    selectedDocumentId,
    selectedTableDocument,
    isLoadingSelectedDocument,
    elasticsearchHighlightsMap,
    rawSearchData,
    contentFilter,
    viewMode,
  })
  const [isCardPreviewDismissed, setIsCardPreviewDismissed] = useState(false)
  const [isTablePreviewDismissed, setIsTablePreviewDismissed] = useState(false)
  const activeDocumentId = activeDocument?.id

  useEffect(() => {
    setIsCardPreviewDismissed(false)
    setIsTablePreviewDismissed(false)
  }, [activeDocumentId, contentFilter, viewMode])

  const handleOpenPreview = useCallback(() => {
    setIsCardPreviewDismissed(false)
    setIsTablePreviewDismissed(false)
  }, [])

  const shouldShowPreview =
    viewMode === 'card'
      ? isPreviewOpen && !isCardPreviewDismissed
      : isPreviewOpen && !isTablePreviewDismissed

  const handleClosePreview = useCallback(() => {
    if (viewMode === 'card') {
      setIsCardPreviewDismissed(true)
      return
    }

    setIsTablePreviewDismissed(true)
  }, [viewMode])

  const handleFragmentClick = useCallback(
    (pageNum: number, fragmentHtml: string) => {
      if (!activeDocument) return
      onOpenNewTab({
        documentId: activeDocument.id,
        fragmentHighlight: { pageNum, fragmentText: fragmentHtml },
        searchOriginQuery: searchQuery,
      })
    },
    [activeDocument, onOpenNewTab, searchQuery]
  )

  const documentPreviewPanelProps: Omit<
    DocumentPreviewPanelProps,
    'onAddToContext'
  > = {
    document: activeDocument,
    isLinkedDocsMode,
    isOpen: shouldShowPreview,
    isLoading: isLoadingPreview,
    onClose: handleClosePreview,
    onDocumentNavigate: onSearchDocumentNavigate,
    highlights: activeDocumentHighlights,
    relevanceScore,
    detailScores,
    showIndividualScores,
    onFragmentClick: handleFragmentClick,
  }

  return {
    activeDocument,
    detailScores,
    documentPreviewPanelProps,
    handleOpenPreview,
    isLoadingPreview,
    isPreviewOpen: shouldShowPreview,
  }
}
